/**
 * iter-338k: PublicGanttView — read-only Gantt for tokenized public links.
 * Reachable at /p/gantt/:token. Renders a simple horizontal bar list (since
 * the full Gantt component is heavy + admin-coupled). Each task bar is
 * positioned proportionally between the earliest start_date and latest end
 * date across the project.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, AlertCircle, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function toDate(v) { return v ? new Date(v) : null; }
function dayDiff(a, b) { return Math.max(1, Math.round((b - a) / 86400000)); }
function fmt(d) { return d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'; }

const STATUS_COLOR = {
  not_started: 'bg-stone-300',
  in_progress: 'bg-blue-500',
  completed: 'bg-emerald-500',
  blocked: 'bg-red-500',
  on_hold: 'bg-amber-500',
};

export default function PublicGanttView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/gantt/${token}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Link is invalid, revoked, or expired.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (loading) return <div className="p-8 text-center text-stone-500">Loading timeline…</div>;
  if (error || !data) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <Card className="max-w-md w-full" data-testid="public-gantt-error">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-700">
              <AlertCircle size={20} />Cannot Open Gantt
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-stone-600">
            {error || 'Unknown error'}
          </CardContent>
        </Card>
      </div>
    );
  }

  const tasks = (data.tasks || []).filter((t) => t.start_date || t.deadline);
  const datedTasks = tasks.map((t) => {
    const s = toDate(t.start_date) || toDate(t.deadline);
    const e = toDate(t.deadline) || toDate(t.start_date);
    return { ...t, _start: s, _end: e && e > s ? e : new Date(s.getTime() + 86400000) };
  });
  const projectStart = datedTasks.length ? new Date(Math.min(...datedTasks.map((t) => t._start.getTime()))) : new Date();
  const projectEnd = datedTasks.length ? new Date(Math.max(...datedTasks.map((t) => t._end.getTime()))) : new Date();
  const totalDays = dayDiff(projectStart, projectEnd);

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-6xl mx-auto px-4 py-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h1 className="text-xl font-bold text-stone-900" data-testid="public-gantt-title">Project Timeline</h1>
              <p className="text-sm text-stone-600">{data.project?.name}{data.project?.client_name ? ` · ${data.project.client_name}` : ''}</p>
            </div>
            <div className="text-xs text-stone-500 text-right">
              <div><Calendar size={12} className="inline mr-1" />{fmt(projectStart)} → {fmt(projectEnd)}</div>
              {data.shared_by && <div className="mt-0.5">Shared by {data.shared_by}</div>}
              {data.expires_at && <div className="mt-0.5"><Clock size={11} className="inline mr-1" />Expires {data.expires_at.slice(0, 10)}</div>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <Card data-testid="public-gantt-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-[11px] text-stone-500 mb-2">
              <span className="w-3 h-3 rounded-sm bg-stone-300 inline-block" /> Not Started
              <span className="w-3 h-3 rounded-sm bg-blue-500 inline-block ml-3" /> In Progress
              <span className="w-3 h-3 rounded-sm bg-emerald-500 inline-block ml-3" /> Completed
              <span className="w-3 h-3 rounded-sm bg-amber-500 inline-block ml-3" /> On Hold
              <span className="w-3 h-3 rounded-sm bg-red-500 inline-block ml-3" /> Blocked
            </div>
            <div className="space-y-1" data-testid="public-gantt-bars">
              {datedTasks.length === 0 && (
                <p className="text-sm text-stone-500 py-6 text-center">No tasks have start/end dates yet.</p>
              )}
              {datedTasks.map((t) => {
                const startOffset = dayDiff(projectStart, t._start) - 1;
                const dur = dayDiff(t._start, t._end);
                const leftPct = (startOffset / totalDays) * 100;
                const widthPct = Math.max(1.5, (dur / totalDays) * 100);
                const colorClass = STATUS_COLOR[t.status] || 'bg-stone-400';
                return (
                  <div key={t.id} className="grid grid-cols-[260px_1fr] gap-2 items-center text-xs py-1 border-b border-stone-100 last:border-0" data-testid={`public-gantt-task-${t.id}`}>
                    <div className="truncate">
                      <div className="font-medium text-stone-800 truncate" title={t.title}>{t.title}</div>
                      <div className="text-stone-500 text-[10px]">{fmt(t._start)} → {fmt(t._end)} · {t.progress || 0}%</div>
                    </div>
                    <div className="relative h-5 bg-stone-100 rounded">
                      <div
                        className={`absolute h-full rounded ${colorClass} opacity-90`}
                        style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                        title={`${t.title} · ${t.progress || 0}%`}
                      >
                        {(t.progress || 0) > 0 && (
                          <div className="absolute inset-y-0 left-0 bg-black/20 rounded-l" style={{ width: `${Math.min(100, t.progress || 0)}%` }} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
        <p className="text-[10px] text-stone-400 text-center mt-4">Read-only view · Generated by Storage Estimator</p>
      </div>
    </div>
  );
}
